import { SettingsApi } from '@itsrever/rever-api'
import { apiClient } from '@/infrastructure/api'
import {
  ExchangesKind,
  ExchangesSettings,
  GroupExchanges
} from '@/domain/exchanges'
import { PB_KINDS, toGroupExchanges, toPbSettings } from './dto'

const settingsApi = new SettingsApi(undefined, undefined, apiClient)

async function findGroupExchanges(
  group: string,
  signal?: AbortSignal
): Promise<GroupExchanges> {
  const response = await settingsApi.getGroupExchangesSettingsV2(
    { ecommerceGroup: group },
    { signal }
  )
  return toGroupExchanges(response.data)
}

async function saveGroupExchanges(
  group: string,
  kind: ExchangesKind,
  value: ExchangesSettings
) {
  await settingsApi.saveGroupExchangesSettingsV2({
    ecommerceGroup: group,
    pbSaveGroupExchangesSettingsV2Request: {
      ecommerce_group: group,
      settings: toPbSettings(kind, value)
    }
  })
}

async function saveStoreExchanges(
  ecommerceId: string,
  kind: ExchangesKind,
  value: ExchangesSettings
) {
  await settingsApi.saveEcommerceExchangesSettingsV2({
    ecommerceId,
    pbSaveEcommerceExchangesSettingsV2Request: {
      ecommerce_id: ecommerceId,
      settings: toPbSettings(kind, value)
    }
  })
}

async function resetStoreExchanges(ecommerceId: string, kind: ExchangesKind) {
  await settingsApi.deleteExchangesSettingsOverridesV2({
    ecommerceId,
    kinds: [PB_KINDS[kind]]
  })
}

// The title the portal shows above a product's extra details lives in the portal copies, per portal and language.
const EXTRA_DETAILS_TAG = 'full_catalog_extra_details'

async function findExtraDetailsTitles(
  ecommerceId: string,
  signal?: AbortSignal
): Promise<Record<string, string>> {
  const response = await settingsApi.getECommerceTextCopiesV2(
    { ecommerceId },
    { signal }
  )
  return Object.fromEntries(
    (response.data.language_copies ?? []).flatMap(({ language, copies }) => {
      const text = copies?.find((copy) => copy.tag === EXTRA_DETAILS_TAG)?.text
      return language && text ? [[language, text]] : []
    })
  )
}

async function saveExtraDetailsTitles(
  ecommerceIds: string[],
  titles: Record<string, string>
) {
  const copies = Object.entries(titles).map(([lang_tag, text]) => ({
    lang_tag,
    text
  }))
  await Promise.all(
    ecommerceIds.map((ecommerceId) =>
      settingsApi.updateCopies({
        pbUpdateEcommerceCopyRequest: {
          ecommerce_id: ecommerceId,
          copy_details: { [EXTRA_DETAILS_TAG]: { copies } }
        }
      })
    )
  )
}

export default {
  findGroupExchanges,
  saveGroupExchanges,
  saveStoreExchanges,
  resetStoreExchanges,
  findExtraDetailsTitles,
  saveExtraDetailsTitles
}
